import React, { useState } from "react";
import { Mail, MapPin, Phone } from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";

const FOUNDERS = [
  {
    name: "Sawaisingh Rajpurohit",
    role: "Founder & Creative Visionary",
    description: "Leads the vision, branding, product ideas, and future growth of SSR Innovations.",
    initials: "SR",
  },
  {
    name: "Mahendra Singh Rajpurohit",
    role: "Co-Founder & Operations",
    description: "Manages business operations, legal processes, partnerships, and company administration.",
    initials: "MR",
  },
];

export default function ContactPage() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });

  async function submit(e) {
    e.preventDefault();
    toast.success("Thanks! We'll get back to you within 24h.");
    setForm({ name: "", email: "", message: "" });
  }

  return (
    <>
      {/* Founders section */}
      <section className="max-w-6xl mx-auto px-6 md:px-10 pt-16 pb-4" data-testid="founders-section">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, ease: "easeOut" }}
        >
          <div className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">Leadership</div>
          <h2 className="mt-2 font-heading font-medium text-4xl sm:text-5xl tracking-tighter">Meet the Founders</h2>
          <p className="mt-4 max-w-2xl text-muted-foreground leading-relaxed">
            SSR Innovations was founded with a vision to create premium technology and lifestyle products with innovation, quality, and trust.
          </p>
        </motion.div>

        <div className="mt-10 grid md:grid-cols-2 gap-5 md:gap-6">
          {FOUNDERS.map((f, i) => (
            <motion.article
              key={f.name}
              data-testid={`founder-card-${f.initials.toLowerCase()}`}
              initial={{ opacity: 0, y: 32 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.6, delay: 0.1 + i * 0.12, ease: "easeOut" }}
              className="group relative rounded-3xl p-8 overflow-hidden border border-white/10 dark:border-white/10 bg-white/[0.04] dark:bg-white/[0.03] backdrop-blur-xl transition-all duration-500 hover:border-electric/60 hover:shadow-[0_0_40px_-4px_rgba(0,85,255,0.45)]"
            >
              {/* Ambient electric-blue glow that appears on hover */}
              <div className="pointer-events-none absolute -inset-px rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                style={{ background: "radial-gradient(600px circle at 30% 0%, rgba(0,85,255,0.18), transparent 60%)" }} />

              <div className="relative flex items-center gap-5">
                <div className="relative flex-shrink-0">
                  <div className="w-20 h-20 rounded-full bg-gradient-to-br from-electric to-electric-600 flex items-center justify-center border border-white/10 shadow-lg">
                    <span className="font-heading font-medium text-2xl text-white tracking-tight">{f.initials}</span>
                  </div>
                  <div className="absolute inset-0 rounded-full ring-1 ring-white/10 group-hover:ring-electric/40 transition-colors" />
                </div>
                <div className="min-w-0">
                  <h3 className="font-heading font-medium text-xl tracking-tight">{f.name}</h3>
                  <div className="mt-1 text-xs font-mono uppercase tracking-[0.18em] text-electric">{f.role}</div>
                </div>
              </div>

              <p className="relative mt-6 text-sm text-muted-foreground leading-relaxed">
                {f.description}
              </p>

              {/* Bottom accent line */}
              <div className="relative mt-6 h-px w-full bg-gradient-to-r from-transparent via-white/10 to-transparent group-hover:via-electric/40 transition-colors" />
            </motion.article>
          ))}
        </div>
      </section>

      {/* Existing contact section */}
      <div className="max-w-6xl mx-auto px-6 md:px-10 py-16 grid md:grid-cols-2 gap-16">
        <div>
          <div className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">Contact</div>
          <h1 className="mt-2 font-heading font-medium text-5xl tracking-tighter">Let's talk.</h1>
          <p className="mt-6 text-muted-foreground">Whether it's product questions, wholesale enquiries, or just to say hi — we'd love to hear from you.</p>
          <div className="mt-10 space-y-4">
            <div className="flex items-center gap-3"><Mail className="w-4 h-4 text-electric" /><a href="mailto:hello@ssr.com" className="text-sm">hello@ssr.com</a></div>
            <div className="flex items-center gap-3"><Phone className="w-4 h-4 text-electric" /><span className="text-sm">+91 80 0000 0000</span></div>
            <div className="flex items-center gap-3"><MapPin className="w-4 h-4 text-electric" /><span className="text-sm">Indiranagar, Bangalore, IN</span></div>
          </div>
        </div>
        <form onSubmit={submit} className="p-8 rounded-2xl border border-border bg-card space-y-4">
          <input data-testid="contact-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Name" className="w-full h-11 px-4 rounded-full bg-secondary text-sm outline-none focus:ring-2 focus:ring-electric" />
          <input data-testid="contact-email" required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email" className="w-full h-11 px-4 rounded-full bg-secondary text-sm outline-none focus:ring-2 focus:ring-electric" />
          <textarea data-testid="contact-message" required rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Message" className="w-full px-4 py-3 rounded-2xl bg-secondary text-sm outline-none focus:ring-2 focus:ring-electric" />
          <button data-testid="contact-submit" className="w-full h-11 rounded-full bg-electric text-white font-medium hover:bg-electric-600 transition-colors">Send message</button>
        </form>
      </div>
    </>
  );
}
